
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { fadeInVariants } from '@/lib/animations';
import { Frown, Meh, Smile, Heart } from 'lucide-react';

// Icons for mood selection
const moodIcons = [
  { value: 1, icon: Frown, label: 'Muito triste', color: 'text-red-500' },
  { value: 2, icon: Frown, label: 'Triste', color: 'text-orange-500' },
  { value: 3, icon: Meh, label: 'Neutro', color: 'text-gray-500' },
  { value: 4, icon: Smile, label: 'Feliz', color: 'text-green-500' },
  { value: 5, icon: Heart, label: 'Muito feliz', color: 'text-pink-500' }
];

const GratitudeJournal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const [selectedMood, setSelectedMood] = useState<number | null>(null);
  const { toast } = useToast();
  
  const handleSubmit = () => {
    if (!text.trim() || !selectedMood) {
      toast({
        title: "Campos incompletos",
        description: "Por favor, preencha sua reflexão e selecione seu humor.",
        variant: "destructive",
      });
      return;
    }
    
    if (text.length > 140) {
      toast({
        title: "Texto muito longo",
        description: "Por favor, limite sua reflexão a 140 caracteres.",
        variant: "destructive",
      });
      return;
    }
    
    // Will be replaced with actual Supabase call later
    console.log('Saving entry:', { text, mood: selectedMood });
    
    toast({
      title: "Reflexão registrada!",
      description: "Até amanhã com uma nova reflexão de gratidão.",
    });
    
    setText('');
    setSelectedMood(null);
    setIsOpen(false);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          className="bg-brand-blue hover:bg-brand-blue/90"
          onClick={() => setIsOpen(true)}
        >
          Registrar Gratidão
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Diário de Gratidão</DialogTitle>
          <DialogDescription>
            Reflita sobre algo positivo que aconteceu hoje ou pelo qual você é grato.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div>
            <Textarea
              placeholder="Por que você é grato hoje? (máx. 140 caracteres)"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={140}
              className="resize-none"
            />
            <p className="text-right text-xs text-muted-foreground mt-1">
              {text.length}/140
            </p>
          </div>
          <div>
            <p className="text-sm mb-2">Como você está se sentindo?</p>
            <div className="flex justify-between">
              {moodIcons.map((mood) => {
                const IconComponent = mood.icon;
                return (
                  <motion.button
                    key={mood.value}
                    whileHover={{ y: -2 }}
                    onClick={() => setSelectedMood(mood.value)}
                    className={`p-2 rounded-full ${
                      selectedMood === mood.value
                        ? 'bg-blue-100 scale-110'
                        : 'hover:bg-gray-100'
                    }`}
                    title={mood.label}
                  >
                    <IconComponent 
                      className={`w-8 h-8 ${
                        selectedMood === mood.value ? 'text-blue-600' : mood.color
                      }`} 
                    />
                  </motion.button>
                );
              })}
            </div>
          </div>
          <Button onClick={handleSubmit} className="w-full mt-2">
            Salvar Reflexão
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GratitudeJournal;
